import { Component, OnInit, OnDestroy } from '@angular/core';
import { BoobleModalService } from 'booble-modal';
import { Subject, throwError } from 'rxjs';
import { catchError, debounceTime, takeUntil } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';
import { NotifierService } from 'angular-notifier';

import { OrdersService } from 'src/app/shared/services/orders.service';
import { InboundOrderDto } from 'src/app/domain/models/dto/inbound-order.dto';

@Component({
  selector: 'app-exchange-documents-check',
  templateUrl: './exchange-documents-check.component.html',
  styleUrls: ['./exchange-documents-check.component.scss'],
})
export class ExchangeDocumentsCheckComponent implements OnInit, OnDestroy {
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  boobleModalData: any;

  public orders: InboundOrderDto[] = [];
  public totalElements: number = 0;
  haveOrders: boolean = false;
  searchText: string = '';
  loading: boolean = false;

  pageSize: number = 10;
  pageNumber: number = 0;

  selectedOrder: InboundOrderDto | null = null;
  pdfSrc: Uint8Array | string | null = null;
  pdfLoading: boolean = false;
  pdfCurrentPage: number = 1;
  pdfTotalPages: number = 0;
  pdfZoom: number = 1.0;

  processingIds: Set<number> = new Set();

  showRejectConfirm: boolean = false;
  orderToReject: InboundOrderDto | null = null;

  constructor(
    private modalService: BoobleModalService,
    private readonly ordersService: OrdersService,
    private readonly notifierService: NotifierService
  ) {}

  ngOnInit() {
    this.fetchOrders();

    this.searchSubject
      .pipe(debounceTime(700), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageNumber = 0;
        this.fetchOrders();
      });
  }

  onSearchChange(search: string) {
    this.searchSubject.next(search);
  }

  fetchOrders() {
    this.loading = true;
    this.orders = [];

    const search = this.searchText?.trim() || undefined;

    this.ordersService
      .getSwiftReceipts(this.pageNumber + 1, this.pageSize, search)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.loading = false;
          this.totalElements = result.data?.totalElements ?? 0;

          if (result.data?.content?.length > 0) {
            this.orders = result.data.content;
            this.haveOrders = true;
          } else {
            this.orders = [];
            this.haveOrders = false;
          }
        },
        error: () => {
          this.loading = false;
          this.haveOrders = false;
        },
      });
  }

  onChangePage(event: PageEvent) {
    this.pageNumber = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchOrders();
  }

  getClientName(order: InboundOrderDto): string {
    return order.corporateAccount?.companyName || order.individualAccount?.name || '';
  }

  getClientDocument(order: InboundOrderDto): string {
    return order.corporateAccount?.cnpj || order.individualAccount?.cpf || '';
  }

  getClientDocumentType(order: InboundOrderDto): string {
    return order.corporateAccount?.cnpj ? 'CNPJ' : 'CPF';
  }

  selectOrder(order: InboundOrderDto) {
    if (this.selectedOrder?.id === order.id) {
      this.selectedOrder = null;
      this.pdfSrc = null;
      return;
    }

    this.selectedOrder = order;
    this.pdfLoading = true;
    this.pdfCurrentPage = 1;
    this.pdfZoom = 1.0;

    const base64 = order.proofOperationFileData;

    if (base64) {
      const raw = atob(base64);
      const bytes = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) {
        bytes[i] = raw.charCodeAt(i);
      }
      this.pdfSrc = bytes;
    } else {
      this.pdfSrc = null;
      this.notifierService.notify('error', 'PDF não disponível para este comprovante.');
    }
    this.pdfLoading = false;
  }

  onPdfLoaded(event: any) {
    this.pdfTotalPages = event.numPages;
  }

  previousPage() {
    if (this.pdfCurrentPage > 1) {
      this.pdfCurrentPage--;
    }
  }

  nextPage() {
    if (this.pdfCurrentPage < this.pdfTotalPages) {
      this.pdfCurrentPage++;
    }
  }

  zoomIn() {
    this.pdfZoom = Math.min(this.pdfZoom + 0.25, 3.0);
  }

  zoomOut() {
    this.pdfZoom = Math.max(this.pdfZoom - 0.25, 0.25);
  }

  resetZoom() {
    this.pdfZoom = 1.0;
  }

  get zoomPercentage(): number {
    return Math.round(this.pdfZoom * 100);
  }

  downloadPdf() {
    if (!this.pdfSrc || !this.selectedOrder) return;

    const blob = new Blob([this.pdfSrc as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comprovante_${this.selectedOrder.id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }

  approveOrder(order: InboundOrderDto, event: Event) {
    event.stopPropagation();
    this.processingIds.add(order.id);

    this.ordersService
      .approveSwiftReceipt(order.id)
      .pipe(
        takeUntil(this.destroy$),
        catchError((err) => {
          this.processingIds.delete(order.id);
          this.notifierService.notify('error', 'Erro ao aprovar o upload.');
          return throwError(() => err);
        })
      )
      .subscribe({
        next: () => {
          const formData = new FormData();
          formData.append('proofOperationLater', 'false');
          formData.append('SendEmail', 'true');

          this.ordersService
            .uploadOrderAttachments(order.id, formData)
            .pipe(
              takeUntil(this.destroy$),
              catchError((err) => {
                this.processingIds.delete(order.id);
                this.notifierService.notify('error', 'Erro ao enviar o e-mail para o cliente.');
                return throwError(() => err);
              })
            )
            .subscribe({
              next: () => {
                this.processingIds.delete(order.id);
                this.notifierService.notify('success', 'Upload realizado com sucesso. O cliente receberá o comprovante por e-mail.');
                this.removeOrderAndSelectNext(order.id);
              },
            });
        },
      });
  }

  rejectOrder(order: InboundOrderDto, event: Event) {
    event.stopPropagation();
    this.orderToReject = order;
    this.showRejectConfirm = true;
  }

  cancelReject() {
    this.showRejectConfirm = false;
    this.orderToReject = null;
  }

  confirmReject() {
    if (!this.orderToReject) return;

    const order = this.orderToReject;
    this.showRejectConfirm = false;
    this.orderToReject = null;
    this.processingIds.add(order.id);

    this.ordersService
      .rejectSwiftReceipt(order.id)
      .pipe(
        takeUntil(this.destroy$),
        catchError((err) => {
          this.processingIds.delete(order.id);
          this.notifierService.notify('error', 'Erro ao reprovar o upload.');
          return throwError(() => err);
        })
      )
      .subscribe({
        next: () => {
          this.processingIds.delete(order.id);
          this.notifierService.notify('success', 'Upload reprovado com sucesso!');
          this.removeOrderAndSelectNext(order.id);
        },
      });
  }

  isProcessing(orderId: number): boolean {
    return this.processingIds.has(orderId);
  }

  private removeOrderAndSelectNext(orderId: number) {
    const currentIndex = this.orders.findIndex((o) => o.id === orderId);
    this.orders = this.orders.filter((o) => o.id !== orderId);
    this.totalElements--;

    if (this.orders.length === 0) {
      this.haveOrders = false;
      this.selectedOrder = null;
      this.pdfSrc = null;
      return;
    }

    if (this.selectedOrder?.id === orderId) {
      this.selectedOrder = null;
      this.pdfSrc = null;
      const nextIndex = Math.min(currentIndex, this.orders.length - 1);
      this.selectOrder(this.orders[nextIndex]);
    }
  }

  closeModal() {
    this.modalService.dismiss(null);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
